import React, { useState, useEffect, useCallback, useRef } from "react";
import { 
  FileText, 
  Save, 
  Eye, 
  Trash2, 
  Plus, 
  Edit, 
  X, 
  Code, 
  ExternalLink,
  Settings,
  Layout,
  Globe,
  Users,
  Menu,
  ChevronDown,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { 
  PageManagement, 
  PageManagementCreateDto, 
  PageManagementUpdateDto,
  pageStatusEnum,
  pageTypeEnum,
  PageStatusObject,
  PageLayoutObject
} from "../../modules/shared/model/page-management.interfaces";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import {
  fetchPageManagements,
  createPageManagement,
  updatePageManagement,
  deletePageManagement,
  clearPageManagementsError,
  setPageManagementFormDirty
} from "../../redux/Slices/PageManagementSlice";

interface PageEditorProps {
  onPageSelect?: (page: PageManagement) => void;
  onPageCreate?: (page: PageManagement) => void;
  onPageUpdate?: (page: PageManagement) => void;
  onPageDelete?: (pageId: string) => void;
}

const PageEditor: React.FC<PageEditorProps> = React.memo(({
  onPageSelect,
  onPageCreate,
  onPageUpdate,
  onPageDelete
}) => {
  const dispatch = useAppDispatch();
  
  // Component identifier for debugging - use useRef to prevent re-creation
  const componentId = useRef(Math.random().toString(36).substr(2, 9));
  console.log(`PageEditor component ${componentId.current} rendered`);
  
  // Redux state
  const {
    pageManagements: pages,
    pageManagementsLoading: loading,
    pageManagementsError: error,
    isFormDirty,
    pageManagementsTotalCount: totalCount,
    pageManagementsCurrentPage: currentPage,
    pageManagementsHasNextPage: hasNextPage,
    pageManagementsHasPreviousPage: hasPreviousPage
  } = useAppSelector((state) => state.pageManagements);

  // Local state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedPage, setSelectedPage] = useState<PageManagement | null>(null);
  const [expandedPage, setExpandedPage] = useState<string | null>(null);
  const [contentType, setContentType] = useState<"text" | "html">("text");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<number | "all">("all");
  const [newKeyword, setNewKeyword] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Ref to track if fetch has been called
  const hasFetched = useRef(false);

  // Form state
  const [formData, setFormData] = useState<PageManagementCreateDto>({
    title: "",
    url: "",
    content: "",
    status: { id: pageStatusEnum.Draft, name: "Draft" },
    type: { id: pageTypeEnum["Default Layout"], name: "Default Layout" },
    enableRoleBased: false,
    isText: true,
    metaKeywords: [],
    metaDescription: ""
  });

  // Fetch pages when component mounts or pagination changes
  useEffect(() => {
    console.log(`PageEditor ${componentId.current} useEffect running, hasFetched:`, hasFetched.current);
    if (!hasFetched.current) {
      console.log(`PageEditor ${componentId.current} Fetching page managements...`);
      hasFetched.current = true;
      dispatch(fetchPageManagements({ 
        page: 1, 
        limit: pageSize,
        filters: {
          title: searchQuery || undefined,
          url: searchQuery || undefined,
          status: statusFilter !== "all" ? statusFilter : undefined
        }
      }));
    }
  }, [dispatch, pageSize]); // Depend on dispatch and pageSize

  // Debounced search effect
  useEffect(() => {
    if (hasFetched.current) {
      // Clear existing timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      
      // Set new timeout for debounced search
      const timeout = setTimeout(() => {
        console.log(`PageEditor ${componentId.current} Refetching due to search/filter change`);
        dispatch(fetchPageManagements({ 
          page: 1, 
          limit: pageSize,
          filters: {
            title: searchQuery || undefined,
            url: searchQuery || undefined,
            status: statusFilter !== "all" ? statusFilter : undefined
          }
        }));
      }, 500); // 500ms debounce
      
      setSearchTimeout(timeout);
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [dispatch, searchQuery, statusFilter, pageSize]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Handle pagination navigation
  const handlePageChange = useCallback((newPage: number) => {
    dispatch(fetchPageManagements({ 
      page: newPage, 
      limit: pageSize,
      filters: {
        title: searchQuery || undefined,
        url: searchQuery || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined
      }
    }));
  }, [dispatch, pageSize, searchQuery, statusFilter]);

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    dispatch(fetchPageManagements({ 
      page: 1, 
      limit: newPageSize,
      filters: {
        title: searchQuery || undefined,
        url: searchQuery || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined
      }
    }));
  }, [dispatch, searchQuery, statusFilter]);

  // Handle form changes
  const handleFormChange = useCallback((field: keyof PageManagementCreateDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    dispatch(setPageManagementFormDirty(true));
  }, [dispatch]);

  // Handle content type change
  const handleContentTypeChange = useCallback((type: "text" | "html") => {
    console.log("Content type changed to:", type, "isText will be:", type === "text");
    setContentType(type);
    handleFormChange("isText", type === "text");
  }, [handleFormChange]);

  // Handle adding a new keyword
  const handleAddKeyword = useCallback(() => {
    if (newKeyword.trim()) {
      const currentKeywords = formData.metaKeywords || [];
      const updatedKeywords = [...currentKeywords, newKeyword.trim()];
      handleFormChange("metaKeywords", updatedKeywords);
      setNewKeyword("");
    }
  }, [newKeyword, formData.metaKeywords, handleFormChange]);


  // Handle create page
  const handleCreatePage = async () => {
    try {
      console.log("Creating page with formData:", formData);
      const result = await dispatch(createPageManagement(formData)).unwrap();
      setShowAddModal(false);
      resetForm();
      onPageCreate?.(result);
    } catch (error) {
      console.error("Failed to create page:", error);
    }
  };

  // Handle save page (for both create and update)
  const handleSavePage = async () => {
    console.log("handleSavePage - Current formData:", formData);
    console.log("handleSavePage - contentType:", contentType);
    console.log("handleSavePage - isText value:", formData.isText);
    
    if (showAddModal) {
      await handleCreatePage();
    } else if (showEditModal) {
      await handleUpdatePage();
    }
  };

  // Handle update page
  const handleUpdatePage = async () => {
    if (!selectedPage) return;
    
    try {
      const updateData: PageManagementUpdateDto = {
        title: formData.title,
        url: formData.url,
        content: formData.content,
        status: {id: Number(formData.status.id), name: formData.status.name},
        type: {id: Number(formData.type.id), name: formData.type.name},
        enableRoleBased: formData.enableRoleBased,
        isText: formData.isText,
        metaKeywords: formData.metaKeywords,
        metaDescription: formData.metaDescription
      };
      
      console.log("Updating page with updateData:", updateData);
      const result = await dispatch(updatePageManagement({ 
        id: selectedPage.id, 
        data: updateData 
      })).unwrap();
      
      setShowEditModal(false);
      setSelectedPage(null);
      resetForm();
      onPageUpdate?.(result);
    } catch (error) {
      console.error("Failed to update page:", error);
    }
  };

  // Handle delete page
  const handleDeletePage = async (pageId: string) => {
    if (!confirm("Are you sure you want to delete this page? This action cannot be undone.")) {
      return;
    }

    try {
      await dispatch(deletePageManagement(pageId)).unwrap();
      onPageDelete?.(pageId);
    } catch (error) {
      console.error("Failed to delete page:", error);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: "",
      url: "",
      content: "",
      status: { id: pageStatusEnum.Draft, name: "Draft" },
      type: { id: pageTypeEnum["Default Layout"], name: "Default Layout" },
      enableRoleBased: false,
      isText: true,
      metaKeywords: [],
      metaDescription: ""
    });
    setContentType("text");
    setNewKeyword("");
    dispatch(setPageManagementFormDirty(false));
  };

  // Open edit modal
  const openEditModal = (page: PageManagement) => {
    setSelectedPage(page);
    setFormData({
      title: page.title || "",
      url: page.url || "",
      content: page.content || "",
      status: page.status || { id: pageStatusEnum.Draft, name: "Draft" },
      type: page.type || { id: pageTypeEnum["Default Layout"], name: "Default Layout" },
      enableRoleBased: page.enableRoleBased || false,
      isText: page.isText || true,
      metaKeywords: page.metaKeywords || [],
      metaDescription: page.metaDescription || ""
    });
    // Set contentType based on page.isText
    setContentType(page.isText ? "text" : "html");
    setNewKeyword("");
    setShowEditModal(true);
  };

  // Open preview
  const openPreview = (page: PageManagement) => {
    window.open(`${window.location.origin}${page.url}`, "_blank");
    return;
    setSelectedPage(page);
    setShowPreview(true);
  };

  // Get status badge color
  const getStatusBadgeColor = (status: PageStatusObject | null) => {
    if (!status) return "bg-gray-100 text-gray-700";
    switch (status.id) {
      case pageStatusEnum.Published:
        return "bg-green-100 text-green-700";
      case pageStatusEnum.Draft:
        return "bg-yellow-100 text-yellow-700";
      case pageStatusEnum.Archived:
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // Get type badge color
  const getTypeBadgeColor = (type: PageLayoutObject | null) => {
    if (!type) return "bg-gray-100 text-gray-700";
    switch (type.id) {
      case pageTypeEnum["Default Layout"]:
        return "bg-blue-100 text-blue-700";
      case pageTypeEnum["Full Width"]:
        return "bg-purple-100 text-purple-700";
      case pageTypeEnum["Landing Page"]:
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Page Management</h2>
          <p className="text-sm text-gray-600">Manage pages for this content type</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Create New Page
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by title or URL..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value === "all" ? "all" : parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            >
              <option value="all">All Status</option>
              <option value={pageStatusEnum.Draft}>Draft</option>
              <option value={pageStatusEnum.Published}>Published</option>
              <option value={pageStatusEnum.Archived}>Archived</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Pages List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading pages...</p>
        </div>
      ) : error ? (
        <Card className="p-6">
          <div className="text-center text-red-600">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>Error loading pages: {error}</p>
            <Button 
              variant="outline" 
              onClick={() => dispatch(fetchPageManagements({
                page: currentPage,
                limit: pageSize,
                filters: {
                  title: searchQuery || undefined,
                  url: searchQuery || undefined,
                  status: statusFilter !== "all" ? statusFilter : undefined
                }
              }))}
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </Card>
      ) : pages.length === 0 ? (
        <Card className="p-8">
          <div className="text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No pages found</p>
            <p className="text-sm mt-2">Create your first page to get started</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {pages.map((page) => (
            <Card key={page.id} className="overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-lg">{page.title}</h3>
                      <Badge variant="outline" className={getStatusBadgeColor(page.status)}>
                        {page.status?.name || "Unknown"}
                      </Badge>
                      <Badge variant="outline" className={getTypeBadgeColor(page.type)}>
                        {page.type?.name || "Unknown"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Globe className="h-4 w-4" />
                        <a
                          href={`${window.location.origin}${page.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        >
                          {page.url}
                        </a>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {page.metaDescription || "No description available"}
                    </p>
                    {page.metaKeywords && page.metaKeywords.length > 0 && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-1">
                          {page.metaKeywords.slice(0, 3).map((keyword, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
                            >
                              {keyword}
                            </span>
                          ))}
                          {page.metaKeywords.length > 3 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500">
                              +{page.metaKeywords.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openPreview(page)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(page)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePage(page.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {pages.length > 0 && totalCount > pageSize && (
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Page Info */}
            <div className="text-sm text-gray-600 flex items-center gap-2">
              {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>}
              {totalCount > 0 ? (
                <>Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} pages</>
              ) : (
                <>No pages found</>
              )}
            </div>
            
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                disabled={loading}
                className="px-2 py-1 border rounded text-sm disabled:opacity-50"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>

            {/* Pagination Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={!hasPreviousPage || loading}
                className="px-2 py-1"
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPreviousPage || loading}
                className="px-2 py-1"
              >
                Previous
              </Button>
              
              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {(() => {
                  const totalPages = Math.ceil(totalCount / pageSize);
                  const maxVisiblePages = 5;
                  const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                  
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                        disabled={loading}
                        className="px-2 py-1 min-w-[32px]"
                      >
                        {i}
                      </Button>
                    );
                  }
                  return pages;
                })()}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNextPage || loading}
                className="px-2 py-1"
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.ceil(totalCount / pageSize))}
                disabled={!hasNextPage || loading}
                className="px-2 py-1"
              >
                Last
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">
                  {showAddModal ? "Create New Page" : "Edit Page"}
                </h2>
                <button 
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedPage(null);
                    resetForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Page Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleFormChange("title", e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Enter page title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL Path *
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500 text-sm">{window.location.origin}</span>
                    <input
                      type="text"
                      value={formData.url}
                      onChange={(e) => handleFormChange("url", e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      placeholder="/path-to-page"
                    />
                  </div>
                </div>
              </div>

              {/* Status and Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status?.id || pageStatusEnum.Draft}
                    onChange={(e) => {
                      const statusId = parseInt(e.target.value);
                      const statusName = e.target.options[e.target.selectedIndex].text;
                      handleFormChange("status", { id: statusId, name: statusName });
                    }}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value={pageStatusEnum.Draft}>Draft</option>
                    <option value={pageStatusEnum.Published}>Published</option>
                    <option value={pageStatusEnum.Archived}>Archived</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Layout
                  </label>
                  <select
                    value={formData.type?.id || pageTypeEnum["Default Layout"]}
                    onChange={(e) => {
                      const typeId = parseInt(e.target.value);
                      const typeName = e.target.options[e.target.selectedIndex].text;
                      handleFormChange("type", { id: typeId, name: typeName });
                    }}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value={pageTypeEnum["Default Layout"]}>Default Layout</option>
                    <option value={pageTypeEnum["Full Width"]}>Full Width</option>
                    <option value={pageTypeEnum["Landing Page"]}>Landing Page</option>
                  </select>
                </div>
              </div>

              {/* Content */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Content *
                  </label>
                  <div className="flex rounded-md overflow-hidden border">
                    <button
                      className={`px-3 py-1 text-xs ${contentType === "text" ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                      onClick={() => handleContentTypeChange("text")}
                    >
                      Text
                    </button>
                    <button
                      className={`px-3 py-1 text-xs ${contentType === "html" ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                      onClick={() => handleContentTypeChange("html")}
                    >
                      HTML
                    </button>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  {contentType === "html" && <Code className="h-5 w-5 text-gray-400 mt-2" />}
                  <textarea
                    value={formData.content}
                    onChange={(e) => handleFormChange("content", e.target.value)}
                    rows={10}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${contentType === "html" ? "font-mono" : ""}`}
                    placeholder={contentType === "html" ? "Enter HTML content" : "Enter page content"}
                  />
                </div>
                {contentType === "html" && (
                  <p className="text-xs text-gray-500 mt-1">You can use HTML tags to format your content.</p>
                )}
              </div>

              {/* SEO Metadata */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">SEO Metadata</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Meta Description</label>
                    <textarea
                      value={formData.metaDescription || ""}
                      onChange={(e) => handleFormChange("metaDescription", e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="Enter meta description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Keywords</label>
                    <div className="space-y-3">
                      
                      {/* Individual keyword input */}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newKeyword}
                          onChange={(e) => setNewKeyword(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddKeyword();
                            }
                          }}
                          className="flex-1 px-3 py-2 border rounded-lg text-sm"
                          placeholder="Add individual keyword"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleAddKeyword}
                          disabled={!newKeyword.trim()}
                        >
                          Add
                        </Button>
                      </div>
                      
                      {/* Display current keywords */}
                      {formData.metaKeywords && formData.metaKeywords.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs text-gray-500">Current keywords:</p>
                          <div className="flex flex-wrap gap-1">
                            {formData.metaKeywords.map((keyword, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                              >
                                {keyword}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newKeywords = formData.metaKeywords?.filter((_, i) => i !== index) || [];
                                    handleFormChange("metaKeywords", newKeywords);
                                  }}
                                  className="ml-1 text-blue-600 hover:text-blue-800"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>


              {/* Actions */}
              <div className="pt-6 border-t flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedPage(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSavePage}
                  disabled={!formData.title || !formData.url || !formData.content}
                >
                  {showAddModal ? "Create Page" : "Update Page"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && selectedPage && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Page Preview</h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`${window.location.origin}${selectedPage.url}`, "_blank")}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in Browser
                  </Button>
                  <button 
                    onClick={() => setShowPreview(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="prose max-w-none">
                <h1>{selectedPage.title}</h1>
                {(() => {
                  // Check if content starts with HTML tags (even if isText is true)
                  const content = selectedPage.content || "";
                  const startsWithHtmlTag = /^\s*<[^>]+>/.test(content);
                  
                  // If isText is false OR content starts with HTML tags, render as HTML
                  if (!selectedPage.isText || startsWithHtmlTag) {
                    return (
                      <div 
                        dangerouslySetInnerHTML={{ __html: content }}
                        className="mt-4"
                      />
                    );
                  }
                  
                  // Otherwise render as plain text
                  return (
                    <p className="mt-4 text-gray-800 leading-relaxed whitespace-pre-wrap">
                      {content}
                    </p>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

PageEditor.displayName = "PageEditor";

export default PageEditor;
